import { useJsApiLoader } from '@react-google-maps/api';
import googleMapConfig from 'config/google_map.config';
import _ from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import { Loader } from 'zComponents/atoms';
import { Select } from 'zui/Antd';

const { Option } = Select;

const libraries = ['places'];

const SelectAddressAutoComplete = ({ onChange, value: externalValue }) => {
	const [searchText, setSearchText] = useState(externalValue || null);
	const [suggestions, setSuggestions] = useState([]);
	const [loading, setLoading] = useState(false);
	const placesServiceRef = useRef(null);

	const { isLoaded } = useJsApiLoader({
		googleMapsApiKey: googleMapConfig.apiKeyAutocomplete,
		libraries
	});

	const debouncedFetchSuggestions = useMemo(
		() =>
			_.debounce((value) => {
				if (!value || value.length < 3) {
					setSuggestions([]);
					return;
				}

				const autocompleteService = new window.google.maps.places.AutocompleteService();
				setLoading(true);
				autocompleteService.getPlacePredictions(
					{
						input: value,
						componentRestrictions: { country: 'au' }
					},
					(predictions, status) => {
						if (status === window.google.maps.places.PlacesServiceStatus.OK) {
							setSuggestions(predictions || []);
						} else {
							setSuggestions([]);
						}
						setLoading(false);
					}
				);
			}, 500),
		[setSuggestions, setLoading]
	);

	const handleSelect = (placeId) => {
		if (!placesServiceRef.current) {
			placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
		}

		placesServiceRef.current.getDetails(
			{
				placeId,
				fields: ['formatted_address', 'geometry', 'address_components', 'name']
			},
			(place, status) => {
				if (status === window.google.maps.places.PlacesServiceStatus.OK) {
					const getComponent = (type) => {
						const comp = place.address_components?.find((c) => c.types.includes(type));
						return comp ? comp.long_name : '';
					};

					const addressData = {
						addressLine1: `${getComponent('street_number')} ${getComponent('route')}`.trim(),
						suburb: getComponent('locality') || getComponent('sublocality'),
						state: getComponent('administrative_area_level_1'),
						country: getComponent('country'),
						postCode: getComponent('postal_code'),
						latitude: place.geometry.location.lat(),
						longitude: place.geometry.location.lng(),
						formattedAddress: place.formatted_address,
						placeName: place.name
					};

					onChange(addressData);
				} else {
					console.error('Error fetching place details:', status);
				}
			}
		);
	};

	if (!isLoaded) {
		return <Loader />;
	}

	return (
		<Select
			showSearch
			value={searchText}
			placeholder='Search for an address'
			defaultActiveFirstOption={false}
			filterOption={false}
			onSearch={(value) => {
				setSearchText(value);
				debouncedFetchSuggestions(value);
			}}
			onChange={(value) => {
				const selectedSuggestion = suggestions.find((s) => s.place_id === value);
				setSearchText(selectedSuggestion?.description || '');
				handleSelect(value);
			}}
			notFoundContent={loading ? 'Loading...' : 'No results'}
			style={{ width: '100%', fontWeight: 'normal' }}
		>
			{suggestions.map((suggestion) => (
				<Option key={suggestion.place_id} value={suggestion.place_id}>
					{suggestion.description}
				</Option>
			))}
		</Select>
	);
};

export default SelectAddressAutoComplete;
